import { googleAdSuitabilityForTitleId, titleIdFromWatchPath } from './googleAdSuitability.js';

const RUNTIME_KEY = '__WATCHVIM_ADSENSE_SAFETY__';

export const FORBIDDEN_GOOGLE_AD_PREFIXES = Object.freeze([
  '/login',
  '/signup',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/profile',
  '/membership',
  '/checkout',
  '/rent/',
  '/search',
  '/continue-watching',
  '/filmmakers',
  '/support',
  '/contact',
  '/privacy',
  '/cookie-policy',
  '/terms',
  '/refund-policy',
  '/dmca',
  '/accessibility',
  '/advertise',
  '/submit',
  '/casting',
  '/careers',
  '/tv-activate',
  '/redeem-code',
  '/trailer/',
]);

function runtimeState() {
  if (typeof window === 'undefined') {
    return {
      consentEligible: false,
      pageEligible: false,
      requestNonPersonalizedAds: true,
      consentReason: 'server',
      pageReason: 'server',
    };
  }
  if (!window[RUNTIME_KEY]) {
    window[RUNTIME_KEY] = {
      consentEligible: false,
      pageEligible: false,
      requestNonPersonalizedAds: true,
      consentReason: 'undecided',
      pageReason: 'not-evaluated',
    };
  }
  return window[RUNTIME_KEY];
}

function applyRuntimeSafety() {
  if (typeof window === 'undefined') return;
  const state = runtimeState();
  window.adsbygoogle = window.adsbygoogle || [];
  window.adsbygoogle.requestNonPersonalizedAds = state.requestNonPersonalizedAds ? 1 : 0;
  window.adsbygoogle.pauseAdRequests = state.consentEligible && state.pageEligible ? 0 : 1;

  if (typeof document !== 'undefined') {
    document.documentElement.dataset.watchvimAdsenseSafety = state.consentEligible && state.pageEligible ? 'eligible' : 'blocked';
    document.documentElement.dataset.watchvimAdsenseConsent = state.consentReason;
    document.documentElement.dataset.watchvimAdsensePage = state.pageReason;
    window.dispatchEvent?.(new Event('watchvim:google-ad-safety'));
  }
}

export function setAdsenseConsentSafety(preferences = {}) {
  const state = runtimeState();
  const decided = preferences.decided === true;
  const advertising = decided && preferences.advertising === true;
  const personalized = advertising && preferences.personalization === true;
  state.consentEligible = advertising;
  state.requestNonPersonalizedAds = !personalized;
  state.consentReason = !decided ? 'undecided' : advertising ? (personalized ? 'personalized' : 'non-personalized') : 'rejected';
  applyRuntimeSafety();
  return { ...state };
}

export function setAdsensePageSafety(result = {}) {
  const state = runtimeState();
  state.pageEligible = result.eligible === true;
  state.pageReason = String(result.reason || (state.pageEligible ? 'eligible' : 'blocked'));
  applyRuntimeSafety();
  return { ...state };
}

export function isAdsenseCandidateRoute(pathname = '') {
  const path = String(pathname || '').split('?')[0].split('#')[0];
  if (FORBIDDEN_GOOGLE_AD_PREFIXES.some((prefix) => path === prefix || path.startsWith(prefix))) return false;
  if (/^\/watch\/[^/]+\/?$/.test(path)) return true;
  return /^\/watch\/[^/]+\/season\/[^/]+\/episode\/[^/]+\/?$/.test(path);
}

function activeGoogleCompanion(surface) {
  const box = surface?.querySelector?.('.watchvim-companion-box[data-adsense-status]');
  const status = String(box?.getAttribute?.('data-adsense-status') || '').toLowerCase();
  return status === 'loading' || status === 'adsense' || status === 'gam';
}

export function evaluateAdsensePageSafety({ pathname = '', root = null, visibilityState = '' } = {}) {
  const documentRoot = root || (typeof document !== 'undefined' ? document : null);
  const currentVisibility = visibilityState || (typeof document !== 'undefined' ? document.visibilityState : 'hidden');

  if (!isAdsenseCandidateRoute(pathname)) return { eligible: false, reason: 'forbidden-route' };
  if (currentVisibility !== 'visible') return { eligible: false, reason: 'page-hidden' };

  const titleId = titleIdFromWatchPath(pathname);
  const suitability = googleAdSuitabilityForTitleId(titleId);
  if (!suitability.googleDemandEligible) {
    return { eligible: false, reason: `title-${suitability.classification}` };
  }

  const surface = documentRoot?.querySelector?.('.playback-page[data-ad-mode="avod"]');
  if (!surface) return { eligible: false, reason: 'not-avod-playback' };

  const playbackId = String(surface.getAttribute('data-playback-id') || '').trim();
  if (!playbackId) return { eligible: false, reason: 'missing-publisher-content' };

  // A playback ID is only an asset reference, not proof that publisher content is on screen.
  // Keep Google display requests paused through loading, the start gate and video ad breaks.
  if (surface.getAttribute('data-avod-phase') !== 'content' || surface.getAttribute('data-preroll-complete') !== 'true') {
    return { eligible: false, reason: 'publisher-video-not-started' };
  }
  if (surface.querySelector('.playback-fallback.inline, .playback-error')) {
    return { eligible: false, reason: 'publisher-playback-error' };
  }
  if (!surface.querySelector('.watchvim-content-player') || surface.querySelector('.playback-player-loading')) {
    return { eligible: false, reason: 'publisher-player-not-mounted' };
  }
  if (!activeGoogleCompanion(surface)) return { eligible: false, reason: 'no-google-companion' };

  return { eligible: true, reason: 'visible-standard-avod-companion' };
}

export function forceAdsensePaused(reason = 'forced-pause') {
  return setAdsensePageSafety({ eligible: false, reason });
}

export function readAdsenseSafetyState() {
  return { ...runtimeState() };
}

